import React, { useState } from 'react'
import { TableHeading, Orderable } from '@types'
import { DraggableTableHeader } from '@components/common'
import Loader from '@components/common/Loader'
import YSpacing from '@components/common/YSpacing'
import { Dropdown } from '@components/common/form'

interface OrderableTableProps {
  orderables: Orderable[]
  isLoading: boolean
  renderDataCell: (
    orderable: Orderable,
    heading: TableHeading,
  ) => React.ReactNode
}

const OrderableHistoryTable: React.FC<OrderableTableProps> = ({
  orderables,
  isLoading,
  renderDataCell,
}) => {
  const [tableHeadings, setTableHeadings] =
    useState<TableHeading[]>(initialTableHeadings)
  const Colspan = tableHeadings.length

  const onChangeTableHeading = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const headingToAdd = initialTableHeadings.find(
      (heading) => heading.id === value,
    )
    if (
      headingToAdd &&
      !tableHeadings.some((heading) => heading.id === value)
    ) {
      const newHeadings = [...tableHeadings, headingToAdd]
      setTableHeadings(newHeadings)
    }
  }

  const renderHeadingsDropdown = () => {
    const tableIds = tableHeadings.map((heading) => heading.id)
    const notSelected = initialTableHeadings.filter(
      (heading) => !tableIds.includes(heading.id),
    )
    if (notSelected.length === 0) {
      return <></>
    }

    return (
      <div className="ml-6">
        <Dropdown
          label=""
          width="200px"
          marginBottom="0"
          value=""
          onChange={onChangeTableHeading}
        >
          <option value="" disabled hidden>
            - Add Column -
          </option>
          <option />
          {notSelected.map((value) => (
            <option key={value.id} value={value.id}>
              {value.label}
            </option>
          ))}
        </Dropdown>
        <YSpacing height="15px" />
      </div>
    )
  }

  return (
    <>
      {renderHeadingsDropdown()}
      <table className="table page bg-white mx-5 w-auto">
        <thead>
          <DraggableTableHeader
            headings={tableHeadings}
            updateHeadings={(headings: TableHeading[]) =>
              setTableHeadings(headings)
            }
          />
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={Colspan}>
                <Loader />
              </td>
            </tr>
          )}
          {!isLoading && orderables.length === 0 && (
            <tr>
              <td colSpan={Colspan} className="no-promocodes">
                No results
              </td>
            </tr>
          )}
          {!isLoading &&
            orderables.map((orderable, rowIndex) => (
              <tr key={rowIndex}>
                {tableHeadings.map((heading, cellIndex) => (
                  <td key={cellIndex}>{renderDataCell(orderable, heading)}</td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </>
  )
}

export default OrderableHistoryTable

const initialTableHeadings: TableHeading[] = [
  {
    id: 'orderNumber',
    label: 'Number',
    key: 'orderNumber',
  },
  {
    id: 'dateMoment1',
    label: 'Event Date',
    key: 'dateMoment',
    format: 'MMM Do YYYY',
  },
  {
    id: 'dateMoment2',
    label: 'Time',
    key: 'dateMoment',
    format: 'h:mm a',
  },
  {
    id: 'headCount',
    label: 'Head Count',
    key: 'headCount',
  },
  {
    id: 'chefName',
    label: 'Chef',
    key: 'chefName',
  },
  {
    id: 'total',
    label: 'Total',
    key: 'total',
  },
  {
    id: 'dropoffAddress',
    label: 'Delivery Address',
    key: 'dropoffAddress',
  },
  {
    id: 'dinerProfile',
    label: 'Diner Profile',
    key: 'dinerProfileId',
  },
  {
    id: 'clientReportingProgram',
    label: 'Reporting Program',
    key: 'clientReportingProgramId',
  },
  {
    id: 'contactName',
    label: 'Contact Name',
    key: 'contact',
    subKey: 'name',
  },
  {
    id: 'contactEmail',
    label: 'Contact Email',
    key: 'contact',
    subKey: 'email',
  },
  {
    id: 'accountExecutive',
    label: 'Sales Rep',
    key: 'accountExecutive',
  },
  {
    id: 'status',
    label: 'Status',
    key: 'status',
  },
]
